// Sürükle-bırak sıralama saf mantığı (Finar rev-2 #4b/#12b/#14 — Katalog + Veri Yönetimi ağacı).
// Yalnız AYNI üst içinde sıralama: üst değiştirme DnD ile yapılmaz (MetricModal "Konum" kalır).
// Kardeş listesi GÖRÜNÜM sırasıyla verilir (ağaçtaki `childrenOf` — orderIndex → etiket); sonuç
// tüm kardeşleri 0..n-1 yeniden numaralar ve yalnız indeksi DEĞİŞEN satırları döner (↑↓ ile aynı
// sözleşme — `moveAmongSiblings` bunun üstüne kurulu). Bileşen/DOM yok — vitest node ortamı.

export interface ReorderSibling {
  id: string;
  orderIndex: number;
}

export type DropPosition = "before" | "after";

/** İki düğüm aynı üstün çocuğu mu (kök = null; `undefined` üst null sayılır). */
export function isSameParent(
  a: { parentId?: string | null | undefined },
  b: { parentId?: string | null | undefined },
): boolean {
  return (a.parentId ?? null) === (b.parentId ?? null);
}

/** Yeni sıra → yalnız değişen `orderIndex` yazımları (0..n-1 bitişik). */
function renumber(ordered: readonly ReorderSibling[]): Map<string, number> {
  const out = new Map<string, number>();
  ordered.forEach((s, i) => {
    if (s.orderIndex !== i) out.set(s.id, i);
  });
  return out;
}

/**
 * Sürüklenen satırı hedef satırın önüne/arkasına bırakır. Sürüklenen ya da hedef bu kardeş
 * listesinde değilse (farklı üst), kendi üstüne bırakıldıysa ya da sıra değişmiyorsa null.
 */
export function planReorder(
  siblings: readonly ReorderSibling[],
  draggedId: string,
  overId: string,
  position: DropPosition,
): Map<string, number> | null {
  if (draggedId === overId) return null;
  const from = siblings.findIndex((s) => s.id === draggedId);
  if (from < 0 || !siblings.some((s) => s.id === overId)) return null;
  const rest = siblings.filter((s) => s.id !== draggedId);
  const at = rest.findIndex((s) => s.id === overId) + (position === "after" ? 1 : 0);
  const next = [...rest.slice(0, at), siblings[from]!, ...rest.slice(at)];
  if (next.every((s, i) => s.id === siblings[i]!.id)) return null;
  return renumber(next);
}

/** Klavye/↑↓ eşdeğeri: bir adım yukarı (−1) ya da aşağı (+1); uçlarda ve bilinmeyen id'de null. */
export function moveBy(
  siblings: readonly ReorderSibling[],
  id: string,
  direction: -1 | 1,
): Map<string, number> | null {
  const idx = siblings.findIndex((s) => s.id === id);
  const target = siblings[idx + direction];
  if (idx < 0 || !target) return null;
  return planReorder(siblings, id, target.id, direction < 0 ? "before" : "after");
}

/** Yazımları listeye uygular (değişmeyen satır aynı nesne kalır). */
export function applyOrderChanges<T extends ReorderSibling>(
  items: readonly T[],
  changes: ReadonlyMap<string, number>,
): T[] {
  return items.map((i) => {
    const next = changes.get(i.id);
    return next === undefined ? i : { ...i, orderIndex: next };
  });
}

/**
 * Katalog sunucu kuralının (`POST /kpi/metrics/reorder`) istemci eşi: `changes` uygulanmış yeni
 * sırada ETKİN kardeşler 0..n-1 (`orderedIds` = istek gövdesi), kalkmışlar (`deprecated`) mevcut
 * göreli sıralarıyla sona. `indexes` yalnız değişen satırlar (iyimser yerel yazım). Etkin sıra
 * değişmiyorsa null (istek atılmaz).
 */
export interface CatalogReorderPlan {
  orderedIds: string[];
  indexes: Map<string, number>;
}

export function activeOrderPlan(
  siblings: readonly (ReorderSibling & { status: string })[],
  changes: ReadonlyMap<string, number>,
): CatalogReorderPlan | null {
  const isOld = (s: { status: string }): boolean => s.status === "deprecated";
  const next = [...siblings].sort(
    (a, b) => (changes.get(a.id) ?? a.orderIndex) - (changes.get(b.id) ?? b.orderIndex),
  );
  const active = next.filter((s) => !isOld(s));
  const before = siblings.filter((s) => !isOld(s)).map((s) => s.id);
  if (active.every((s, i) => s.id === before[i])) return null;
  const final = [...active, ...siblings.filter(isOld)];
  return { orderedIds: active.map((s) => s.id), indexes: renumber(final) };
}
